import type { CustomAttributeData } from '@risksmart-app/domain/src/types/common.types';
import { DocumentFileType } from '@risksmart-app/domain/src/types/consts/document-file-type';
import { VersionStatus } from '@risksmart-app/domain/src/types/consts/version-status';
import type { ApprovalGatedResult } from '@risksmart-app/trpc/src/helpers/with-approval-workflow';
import type {
  InputMaybe,
  UpdateDocumentVersionMutation,
  Version_Status_Enum,
} from '@risksmart-app/web-graphql-client/generated/graphql';
import {
  Document_File_Type_Enum,
  namedOperations,
  UpdateDocumentVersionDocument,
} from '@risksmart-app/web-graphql-client/generated/graphql';

import { unwrapApprovalGated } from '@/hooks/mutations/unwrapApprovalGated';
import { createMutationHook } from '@/utils/hook-factories';

interface UpdateDocumentVersionSharedInput {
  Id: string;
  LatestModifiedAtTimestamp: string;
  Version: string;
  Status: Version_Status_Enum;
  Summary?: InputMaybe<string>;
  ReasonForReview?: InputMaybe<string>;
  ReviewedBy?: InputMaybe<string>;
  ReviewDate?: InputMaybe<string>;
  NextReviewDate?: InputMaybe<string>;
  CustomAttributeData?: InputMaybe<CustomAttributeData>;
}

type UpdateDocumentVersionTypeInput =
  | { Type: 'file'; FileId: string }
  | { Type: 'html'; Content: string }
  | { Type: 'link'; Link: string };

export type UpdateDocumentVersionInput = UpdateDocumentVersionSharedInput &
  UpdateDocumentVersionTypeInput;

export interface UpdateDocumentVersionOptions {
  /**
   * When set, threads the `x-confirm-change-request` and (optional)
   * `x-requester-comment` headers onto the GraphQL mutation context — the
   * legacy publish/CR flow relies on these. The tRPC path handles change
   * requests via the governed confirm/replay flow instead.
   */
  confirmChangeRequest?: boolean;
  requesterComment?: string;
}

/**
 * Maps the version-status GraphQL enum value to the domain enum the tRPC
 * procedure expects. Both share identical string values; this is a typed
 * lookup rather than a cast.
 */
const toVersionStatus = (status: Version_Status_Enum): VersionStatus => {
  switch (status) {
    case 'archived':
      return VersionStatus.Archived;
    case 'draft':
      return VersionStatus.Draft;
    case 'pending_approval':
      return VersionStatus.PendingApproval;
    case 'published':
      return VersionStatus.Published;
  }
};

interface UpdateDocumentVersionSharedPayload {
  Id: string;
  LatestModifiedAtTimestamp: string;
  Version: string;
  Status: VersionStatus;
  Summary?: InputMaybe<string>;
  ReasonForReview?: InputMaybe<string>;
  ReviewedBy?: InputMaybe<string>;
  ReviewDate?: InputMaybe<string>;
  NextReviewDate?: InputMaybe<string>;
  CustomAttributeData?: InputMaybe<CustomAttributeData>;
}

type UpdateDocumentVersionTRPCPayload = UpdateDocumentVersionSharedPayload &
  (
    | { Type: DocumentFileType.File; FileId: string }
    | { Type: DocumentFileType.Html; Content: string }
    | { Type: DocumentFileType.Link; Link: string }
  );

const toTRPCPayload = (
  variables: UpdateDocumentVersionInput
): UpdateDocumentVersionTRPCPayload => {
  const shared: UpdateDocumentVersionSharedPayload = {
    Id: variables.Id,
    LatestModifiedAtTimestamp: variables.LatestModifiedAtTimestamp,
    Version: variables.Version,
    Status: toVersionStatus(variables.Status),
    Summary: variables.Summary,
    ReasonForReview: variables.ReasonForReview,
    ReviewedBy: variables.ReviewedBy,
    ReviewDate: variables.ReviewDate,
    NextReviewDate: variables.NextReviewDate,
    CustomAttributeData: variables.CustomAttributeData,
  };
  switch (variables.Type) {
    case 'file':
      return {
        ...shared,
        Type: DocumentFileType.File,
        FileId: variables.FileId,
      };
    case 'html':
      return {
        ...shared,
        Type: DocumentFileType.Html,
        Content: variables.Content,
      };
    case 'link':
      return {
        ...shared,
        Type: DocumentFileType.Link,
        Link: variables.Link,
      };
  }
};

const toGraphQLVariables = (input: UpdateDocumentVersionInput) => {
  const shared = {
    Id: input.Id,
    LatestModifiedAtTimestamp: input.LatestModifiedAtTimestamp,
    Status: input.Status,
    Version: input.Version,
    Summary: input.Summary,
    ReasonForReview: input.ReasonForReview,
    ReviewedBy: input.ReviewedBy,
    ReviewDate: input.ReviewDate,
    NextReviewDate: input.NextReviewDate,
    CustomAttributeData: input.CustomAttributeData,
  };
  switch (input.Type) {
    case 'file':
      return {
        ...shared,
        Type: Document_File_Type_Enum.File,
        FileId: input.FileId,
        Content: null,
        Link: null,
      };
    case 'html':
      return {
        ...shared,
        Type: Document_File_Type_Enum.Html,
        FileId: null,
        Content: input.Content,
        Link: null,
      };
    case 'link':
      return {
        ...shared,
        Type: Document_File_Type_Enum.Link,
        FileId: null,
        Content: null,
        Link: input.Link,
      };
  }
};

/**
 * Maps the tRPC response to the GraphQL mutation structure. The legacy
 * GraphQL contract returned `{ affected_rows: number }`; the tRPC response
 * carries `{ Id, ModifiedAtTimestamp }` but consumers only branch on
 * success/error.
 */
const mapTrpcResponseToGraphQL = (_applied: {
  Id: string;
  ModifiedAtTimestamp: string;
}): UpdateDocumentVersionMutation => ({
  updateDocumentVersion: {
    __typename: 'GenericMutationOutput',
    affected_rows: 1,
  },
});

export const useUpdateDocumentVersion = createMutationHook({
  name: 'updateDocumentVersion',
  isChangeRequestGated: true,
  mutationOptions: (trpc, onSuccess) =>
    trpc.frontend.documentFile.update.mutationOptions({ onSuccess }),
  mapInput: toTRPCPayload,
  // RSP-5789 — when the mutation was gated into a change request (created or
  // amended), surface it on the CR read feeds immediately.
  refreshChangeRequestFeedsWhen: (
    result: ApprovalGatedResult<{ Id: string; ModifiedAtTimestamp: string }>
  ) => result.status !== 'applied',
  invalidate: (trpc) => [
    trpc.frontend.documentFile.documentFilesByDocumentId.queryKey(),
    trpc.frontend.documentFile.latestDocumentFile.queryKey(),
    trpc.frontend.documentFile.documentFileById.queryKey(),
    trpc.frontend.documentFile.publicDocumentFiles.queryKey(),
    trpc.frontend.documentFile.latestPublicDocumentFileByDocumentId.queryKey(),
    // COI-471 parity: Apollo twin also evicts `document` (version changes
    // affect the parent document's current-version surfaces).
    trpc.frontend.document.pathKey(),
  ],
  // A confirmed update resolves as `applied` OR raises a CR — both are
  // success. Echo the input Id + timestamp on the CR branch (the version is
  // unchanged).
  mapResponse: (
    result: ApprovalGatedResult<{ Id: string; ModifiedAtTimestamp: string }>,
    input: UpdateDocumentVersionInput
  ) =>
    mapTrpcResponseToGraphQL(
      unwrapApprovalGated(result, {
        Id: input.Id,
        ModifiedAtTimestamp: input.LatestModifiedAtTimestamp,
      })
    ),
  graphqlDocument: UpdateDocumentVersionDocument,
  graphqlVariables: (input: UpdateDocumentVersionInput) => ({
    ...toGraphQLVariables(input),
  }),
  evictFields: [
    'document',
    'document_file',
    'document_file_by_pk',
    'change_request',
  ],
  refetchQueries: [namedOperations.Query.getDocumentFileById],
  apolloContext: (options: UpdateDocumentVersionOptions | undefined) => ({
    headers: {
      'x-confirm-change-request': options?.confirmChangeRequest
        ? 'true'
        : 'false',
      ...(options?.requesterComment
        ? {
            'x-requester-comment': encodeURIComponent(options.requesterComment),
          }
        : {}),
    },
  }),
});
